/**
 * Économie — logique pure (§4 : aucune dépendance rendu, réseau, audio ou i18n).
 *
 * Deux monnaies (§6) :
 *  · 🪙 **pièces** produites en continu, dépensées en bâtiments et en terrains ;
 *  · 💎 **gemmes** jamais produites passivement, uniquement gagnées par jalons.
 *
 * Aucune constante d'équilibrage n'apparaît ici : tout vient de `config/balance.ts` et
 * de `config/buildings.ts`. Ce module calcule, il ne décide pas.
 */

import { ECONOMY } from '@/config/balance';
import { BUILDINGS, buildingByType } from '@/config/buildings';
import { LANDMARKS } from '@/config/landmarks';
import type { BuildingDefinition } from '@/config/buildings';

/** Nombre d'exemplaires possédés, par type de bâtiment. */
export type BuildingCounts = Readonly<Record<string, number>>;

/* ── Coûts ─────────────────────────────────────────────────────────────── */

/**
 * Coût du prochain exemplaire : `base × croissance^possédés`.
 *
 * Arrondi au supérieur pour qu'un prix affiché soit toujours atteignable : voir
 * « 1 234 » et ne pas pouvoir acheter avec 1 234 pièces est le genre de détail qui
 * fait croire à un bug.
 */
export function nextCost(definition: BuildingDefinition, owned: number): number {
  return Math.ceil(definition.baseCost * ECONOMY.costGrowth ** Math.max(0, owned));
}

/**
 * Coût cumulé de `count` exemplaires supplémentaires.
 *
 * Somme d'une suite géométrique, close plutôt qu'itérative : le joueur d'endgame achète
 * par centaines, et une boucle par exemplaire ferait ramer la boutique.
 */
export function bulkCost(definition: BuildingDefinition, owned: number, count: number): number {
  if (count <= 0) return 0;

  const growth = ECONOMY.costGrowth;
  const first = definition.baseCost * growth ** Math.max(0, owned);
  return Math.ceil((first * (growth ** count - 1)) / (growth - 1));
}

/**
 * Nombre maximal d'exemplaires achetables avec un solde donné.
 *
 * Inversion directe de la somme géométrique, sans boucle : à l'endgame la réponse peut
 * dépasser le millier.
 */
export function maxAffordable(
  definition: BuildingDefinition,
  owned: number,
  coins: number,
): number {
  if (coins < nextCost(definition, owned)) return 0;

  const growth = ECONOMY.costGrowth;
  const first = definition.baseCost * growth ** Math.max(0, owned);
  const count = Math.log((coins * (growth - 1)) / first + 1) / Math.log(growth);
  const floored = Math.floor(count);

  // Le logarithme peut déborder d'un exemplaire par arrondi flottant : on vérifie.
  return bulkCost(definition, owned, floored) <= coins ? floored : Math.max(0, floored - 1);
}

/* ── Production ────────────────────────────────────────────────────────── */

export interface ProductionModifiers {
  /** Multiplicateur permanent gagné au prestige (§6). */
  readonly prestigeMultiplier?: number;
  /** Produit des boosts actifs — visites, pubs, événements (§9, §14). */
  readonly boostMultiplier?: number;
  /**
   * Niveau de prestige, distinct du multiplicateur.
   *
   * Il ne multiplie rien : il dit quels types sont OFFERTS d'entrée par les ères
   * (`headStartTypes`), donc quelle est la référence des points de repère. Sans lui, un
   * repère posé en début d'ère 3 vaudrait une maison alors que le joueur démarre avec
   * quatre types débloqués.
   */
  readonly prestigeLevel?: number;
}

/** Production d'un type de bâtiment, avant modificateurs. */
export function productionOfType(definition: BuildingDefinition, owned: number): number {
  return definition.baseProduction * Math.max(0, owned);
}

/**
 * Part MAXIMALE de la production d'une ville que la collection COMPLÈTE peut ajouter.
 *
 * ⚠️ C'est le seul nombre qui empêche les points de repère de raccourcir une ère, et il a
 * été fixé par la mesure, pas par le goût. Sans lui, un joueur qui ouvre une ère avec
 * 460 gemmes plante les quatre repères à la seconde zéro sur une ville d'une maison : ils
 * valaient quinze fois toute sa production, et le premier prestige tombait de **2,24 h à
 * 30 minutes** — la gemme achetait de la progression, ce que le §14 interdit à jamais.
 *
 * 20 % : un coup de pouce qu'on remarque sans qu'il remplace le fait de jouer. Mesuré à
 * cette valeur, le premier prestige passe de 2,24 h à 1,86 h — une avance, pas un
 * raccourci.
 */
export const LANDMARK_MAX_SHARE = 0.2;

/** Somme des facteurs du catalogue. DÉRIVÉE, jamais recopiée (règle 15). */
const TOTAL_FACTOR = LANDMARKS.reduce((total, landmark) => total + landmark.productionFactor, 0);

/**
 * Part de la production de la ville qu'ajoute UN POINT de facteur.
 *
 * Dérivée du plafond et du catalogue : ajouter un cinquième repère demain redistribue
 * automatiquement, et la collection complète reste à 20 % sans qu'on y pense.
 */
export const LANDMARK_SHARE = TOTAL_FACTOR > 0 ? LANDMARK_MAX_SHARE / TOTAL_FACTOR : 0;

/**
 * Production des points de repère possédés.
 *
 * ══ La règle, en une phrase ══════════════════════════════════════════════════
 *
 * **Un repère ajoute une part de ce que la ville produit DÉJÀ**, proportionnelle à son
 * facteur. Il accompagne la partie, il ne la remplace pas — sur une ville naissante il ne
 * rapporte presque rien, et c'est voulu : il n'y a rien à accélérer quand il n'y a rien.
 *
 * Cette formulation en a remplacé une autre, « son facteur fois la production de base du
 * meilleur type débloqué », qui explosait en début d'ère (voir `LANDMARK_MAX_SHARE`). Elle
 * a aussi le mérite de se dire au joueur en une ligne, ce que l'autre n'avait pas.
 *
 * ⚠️ `base` est la production des BÂTIMENTS seuls, avant prestige et avant boosts : c'est
 * ce qui rend le calcul insensible à un ×2 temporaire, donc impossible à faire enfler en
 * enchaînant les accélérations.
 */
export function landmarkProduction(counts: BuildingCounts, _prestigeLevel = 0, base = 0): number {
  let weight = 0;
  for (const landmark of LANDMARKS) {
    weight += landmark.productionFactor * Math.max(0, counts[landmark.type] ?? 0);
  }
  if (weight === 0) return 0;

  return LANDMARK_SHARE * weight * base;
}

/** Production totale en pièces par seconde, modificateurs compris. */
export function productionPerSecond(
  counts: BuildingCounts,
  modifiers: ProductionModifiers = {},
): number {
  let base = 0;

  for (const definition of BUILDINGS) {
    base += productionOfType(definition, counts[definition.type] ?? 0);
  }

  /*
    Les repères s'ajoutent ICI, donc AVANT le multiplicateur de prestige — comme
    n'importe quel bâtiment. C'est la condition posée par le ROADMAP 2.3 pour qu'ils ne
    cassent pas le §14 : ce qu'ils produisent entre dans le calcul du prestige, donc
    l'avance qu'ils donnent se rattrape en jouant.
  */
  base += landmarkProduction(counts, modifiers.prestigeLevel ?? 0, base);

  return base * (modifiers.prestigeMultiplier ?? 1) * (modifiers.boostMultiplier ?? 1);
}

/**
 * Pièces produites pendant un intervalle.
 *
 * Basé sur le temps écoulé et non sur le nombre de frames (§6) : une frame perdue ne
 * doit jamais coûter de production au joueur, et un onglet en arrière-plan ne doit pas
 * ralentir sa ville.
 */
export function earnedOver(productionPerSec: number, elapsedMs: number): number {
  if (!Number.isFinite(elapsedMs) || elapsedMs <= 0) return 0;
  return (productionPerSec * elapsedMs) / 1000;
}

/**
 * Gains hors-ligne : plafonnés à huit heures et réduits par un rendement (§6).
 *
 * Le plafond protège l'intérêt de revenir jouer ; le rendement fait que laisser le jeu
 * fermé ne vaut jamais mieux que d'y jouer.
 */
export interface OfflineEarnings {
  readonly coins: number;
  /** Durée réellement créditée, après plafonnement — c'est elle qu'on affiche. */
  readonly creditedMs: number;
  /** Durée écartée par le plafond, pour l'expliquer honnêtement au joueur. */
  readonly cappedMs: number;
  /**
   * Rendement RÉELLEMENT appliqué.
   *
   * Rendu plutôt que relu depuis `ECONOMY` par l'appelant : depuis la nuit blanche (G§3,
   * A4) le rendement n'est plus une constante, et l'écran de retour lisait la valeur de
   * catalogue — il aurait annoncé 25 % en créditant 50 %. Une interface qui explique un
   * calcul doit lire le calcul, pas le supposer.
   */
  readonly efficiency: number;
}

/**
 * Desserrage ponctuel du plafond et du rendement (nuit blanche, G§3 A4).
 *
 * Passé par l'appelant plutôt que lu dans le snapshot : `economy.ts` calcule, il ne décide
 * pas — c'est `creditReturn` qui sait si la nuit blanche s'applique.
 */
export interface OfflineOptions {
  readonly capMs?: number;
  readonly efficiency?: number;
}

export function offlineEarnings(
  productionPerSec: number,
  awayMs: number,
  options: OfflineOptions = {},
): OfflineEarnings {
  const away = Number.isFinite(awayMs) && awayMs > 0 ? awayMs : 0;
  const capMs = options.capMs ?? ECONOMY.offlineCapMs;
  const efficiency = options.efficiency ?? ECONOMY.offlineEfficiency;
  const creditedMs = Math.min(away, capMs);

  return {
    coins: earnedOver(productionPerSec, creditedMs) * efficiency,
    creditedMs,
    cappedMs: away - creditedMs,
    efficiency,
  };
}

/* ── Terrains ──────────────────────────────────────────────────────────── */

/** Coût de la prochaine extension de terrain (§6). */
export function nextLandCost(purchases: number): number {
  return Math.ceil(ECONOMY.land.baseCost * ECONOMY.land.costGrowth ** Math.max(0, purchases));
}

export function canBuyLand(purchases: number, coins: number): boolean {
  return purchases < ECONOMY.land.maxPurchases && coins >= nextLandCost(purchases);
}

/** Côté de la grille après un nombre donné d'extensions. */
export function gridSideAfter(purchases: number, initialSide: number): number {
  return initialSide + Math.max(0, purchases) * ECONOMY.land.stepPerPurchase;
}

/* ── Déblocage en boutique ─────────────────────────────────────────────── */

/**
 * Nombre de types offerts d'emblée par le niveau de prestige.
 *
 * ══ Pourquoi le prestige DOIT ouvrir quelque chose ══════════════════════════
 *
 * Avant, une nouvelle ère ne donnait qu'un multiplicateur : un nombre qui monte, sans
 * rien de nouveau à l'écran. Le joueur repartait d'une maison et refaisait exactement la
 * même montée, en plus rapide. « Plus vite » est une récompense faible ; « autre chose »
 * en est une vraie.
 *
 * Chaque ère offre donc un type de bâtiment débloqué **dès la première seconde**. Ce
 * n'est pas seulement un gain de temps : c'est ce qui fait qu'une ère ne ressemble pas à
 * la précédente, et c'est exactement ce que le principe premier du §1 demande — à chaque
 * instant, un objectif atteignable qui donne envie de rester.
 *
 * ⚠️ **Ce n'est pas un raccourci de production.** Un type offert reste à PAYER, au prix
 * plein de sa courbe. Le prestige donne l'accès, jamais les pièces : le §14 tient.
 */
export function headStartTypes(prestigeLevel: number): number {
  // Le premier type est acquis à tout le monde ; chaque ère en ajoute un.
  return Math.max(0, Math.floor(prestigeLevel));
}

/**
 * Types de bâtiments disponibles à l'achat.
 *
 * Deux sources, et elles se complètent au lieu de se remplacer :
 *
 * 1. **Le prestige** offre les `1 + niveau` premiers types, sans condition.
 * 2. **La chaîne d'exemplaires** continue au-delà : chaque type suivant demande
 *    `unlockAfter` exemplaires du précédent.
 *
 * La chaîne reprend donc là où le prestige s'arrête, et un joueur de troisième ère qui
 * pose cinq restaurants débloque le suivant comme n'importe qui. Un prestige n'annule pas
 * la progression, il la fait commencer plus loin.
 */
export function unlockedTypes(counts: BuildingCounts, prestigeLevel = 0): readonly string[] {
  const visible: string[] = [];
  const offered = headStartTypes(prestigeLevel);

  for (let i = 0; i < BUILDINGS.length; i++) {
    const definition = BUILDINGS[i];
    if (definition === undefined) break;

    // Le premier type, et ceux offerts par les ères, ne demandent rien.
    if (i === 0 || i <= offered) {
      visible.push(definition.type);
      continue;
    }

    const previous = BUILDINGS[i - 1];
    if (previous === undefined) break;

    if ((counts[previous.type] ?? 0) >= definition.unlockAfter) {
      visible.push(definition.type);
    } else {
      break;
    }
  }

  return visible;
}

/**
 * Ce qui manque pour débloquer un type — la **règle des 5, rendue visible** (ROADMAP 3.3).
 *
 * ══ Pourquoi cette fonction existe ═══════════════════════════════════════════
 *
 * Arbitrage de Benoit : on garde la règle, on la MONTRE. Une carte verrouillée
 * affichait un mur — son prix désaturé et rien d'autre. Le joueur voyait qu'il ne
 * pouvait pas, jamais pourquoi ni combien il lui restait à faire. Or « qu'est-ce que je
 * vise, là ? » est la question du §1, et une carte scellée est précisément l'endroit où
 * elle se pose.
 *
 * ⚠️ **DÉRIVÉE du catalogue, jamais tabulée** (règle 15) : la porte d'un type est
 * toujours le type qui le précède, et son seuil est son propre `unlockAfter`. Une table
 * « type → condition » écrite à la main se désynchroniserait au premier bâtiment inséré
 * au milieu du catalogue — et rien ne le dirait, puisque les deux resteraient
 * cohérentes chacune de leur côté.
 *
 * Rend `null` quand la question ne se pose pas : type inconnu, premier du catalogue, ou
 * déjà offert par le prestige. L'appelant n'affiche alors rien plutôt qu'un « 0/0 ».
 */
export interface UnlockProgress {
  /** Type dont il faut posséder des exemplaires. */
  readonly previousType: string;
  /** Exemplaires possédés de ce type. */
  readonly owned: number;
  /** Exemplaires nécessaires. */
  readonly need: number;
}

export function unlockProgress(
  counts: BuildingCounts,
  type: string,
  prestigeLevel = 0,
): UnlockProgress | null {
  const index = BUILDINGS.findIndex((definition) => definition.type === type);
  if (index <= 0) return null;

  // Offert par une ère : il n'y a rien à accomplir, donc rien à montrer.
  if (index <= headStartTypes(prestigeLevel)) return null;

  const definition = BUILDINGS[index];
  const previous = BUILDINGS[index - 1];
  if (definition === undefined || previous === undefined) return null;

  return {
    previousType: previous.type,
    // Borné au seuil : « 7/5 » sur un type déjà débloqué se lirait comme une erreur.
    owned: Math.min(counts[previous.type] ?? 0, definition.unlockAfter),
    need: definition.unlockAfter,
  };
}

/**
 * Type que la PROCHAINE ère offrira, ou `undefined` si le catalogue est épuisé.
 *
 * Sert à l'annoncer avant le reset : « recommencer, c'est perdre sa ville » est une
 * phrase difficile à entendre tant qu'on ne sait pas ce qu'on gagne en échange.
 */
export function nextEraUnlock(prestigeLevel: number): string | undefined {
  return BUILDINGS[headStartTypes(prestigeLevel) + 1]?.type;
}

export function isUnlocked(type: string, counts: BuildingCounts, prestigeLevel = 0): boolean {
  return unlockedTypes(counts, prestigeLevel).includes(type);
}

/* ── Achat ─────────────────────────────────────────────────────────────── */

export type PurchaseRefusal = 'unknown-type' | 'locked' | 'too-expensive';

export interface PurchaseResult {
  readonly ok: boolean;
  readonly refusal?: PurchaseRefusal;
  readonly cost: number;
  readonly coinsAfter: number;
  readonly ownedAfter: number;
}

/**
 * Tente un achat. Ne modifie rien : rend le résultat, à l'appelant d'appliquer.
 *
 * Le motif du refus est explicite plutôt que booléen — c'est lui qui décide de la
 * réaction du joueur : un bâtiment trop cher secoue la carte (E§1.3), un bâtiment
 * verrouillé ne devrait même pas être cliquable.
 */
export function tryPurchase(type: string, counts: BuildingCounts, coins: number): PurchaseResult {
  const definition = buildingByType(type);
  const owned = counts[type] ?? 0;

  if (definition === undefined) {
    return { ok: false, refusal: 'unknown-type', cost: 0, coinsAfter: coins, ownedAfter: owned };
  }

  const cost = nextCost(definition, owned);

  if (!isUnlocked(type, counts)) {
    return { ok: false, refusal: 'locked', cost, coinsAfter: coins, ownedAfter: owned };
  }

  if (coins < cost) {
    return { ok: false, refusal: 'too-expensive', cost, coinsAfter: coins, ownedAfter: owned };
  }

  return { ok: true, cost, coinsAfter: coins - cost, ownedAfter: owned + 1 };
}
